import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Building2, Globe2, Sparkles, TrendingUp, Users, 
  Calendar, Clock, MapPin, CheckCircle, ExternalLink, Handshake,
  BookOpen, BarChart3, Scale, GraduationCap, FileText
} from 'lucide-react';
import InteractiveBackground from '../components/InteractiveBackground';
import PartnersSection from '../components/PartnersSection';

const HomePage = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const conferenceDate = new Date('2026-04-17T12:00:00');
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = conferenceDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Research methodology features (replacing "Powered by Quantum AI")
  const researchFeatures = [
    {
      icon: BarChart3,
      title: 'Economic Modeling',
      description: 'Analyzing the $295M annual jock tax revenue and its potential housing impact across jurisdictions.'
    },
    {
      icon: Globe2,
      title: 'Comparative Policy Analysis',
      description: 'Studying housing tax models from Austria, Spain, Japan, and beyond to identify best practices.'
    },
    {
      icon: TrendingUp,
      title: 'Social Impact Research',
      description: 'Measuring outcomes for the 96,000 families who need housing daily worldwide.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Empowering underserved communities through accessible housing solutions and policy advocacy.'
    }
  ];

  // Research statistics from Harvard article
  const researchStats = [
    { value: '$295M', label: 'Annual Jock Tax Revenue', source: 'National Taxpayers Union Foundation' },
    { value: '1.6B', label: 'People Lacking Adequate Housing', source: 'UN-Habitat' },
    { value: '96,000', label: 'Homes Needed Daily by 2030', source: 'UN-Habitat' },
    { value: '54%', label: 'US Housing Price Increase (2015-2024)', source: 'OECD' }
  ];

  const countdownUnits = [
    { value: timeLeft.days, label: 'Days' },
    { value: timeLeft.hours, label: 'Hours' },
    { value: timeLeft.minutes, label: 'Minutes' },
    { value: timeLeft.seconds, label: 'Seconds' }
  ];

  return (
    <div className="relative min-h-screen">
      <InteractiveBackground />
      
      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 md:px-12 lg:px-24 pt-24">
        {/* Watermark Cityscape Background */}
        <div 
          className="absolute inset-0 z-0 pointer-events-none"
          aria-hidden="true"
        >
          <div 
            className="absolute inset-0 opacity-[0.55] dark:opacity-[0.65]"
            style={{
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e3d4135c-91f6-4c21-8964-e67cd3b65643/images/fbfec489cf822a3457b54327593b096393084033e21f0b2e3ca49252a83f7b79.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center bottom',
              backgroundRepeat: 'no-repeat',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/50 to-transparent" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Badge */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700"
            data-testid="hero-badge"
          >
            <GraduationCap className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium">Harvard Weatherhead Center Research Initiative</span>
          </div>

          {/* Main Headline */}
          <h1 
            className="font-outfit text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-none mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100"
            data-testid="hero-headline"
          >
            Quantum Housing
            <br />
            <span className="text-gradient-quantum">Conference</span>
          </h1>

          {/* Subheadline - Research focused */}
          <p 
            className="text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200"
            data-testid="hero-subheadline"
          >
            How Athletes' Taxes Can Solve the Global Housing Crisis. A research-driven conference 
            exploring the untapped potential of the Jock Tax to fund affordable housing worldwide.
          </p>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300"
            data-testid="hero-cta"
          >
            <Link
              to="/signup"
              data-testid="register-btn"
              className="group flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all duration-300 hover:-translate-y-1 glow-primary"
            >
              Register for Conference
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="https://epicenter.wcfia.harvard.edu/article/how-athletes-taxes-can-solve-affordable-housing-crisis"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="harvard-article-btn"
              className="flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold text-lg transition-all duration-300 hover:bg-white/10"
            >
              Read the Harvard Article
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Research Statistics Section */}
      <section className="relative z-10 py-20 px-6 md:px-12 lg:px-24" data-testid="research-stats-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              The Evidence
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight">
              Research-Backed <span className="text-gradient-quantum">Statistics</span>
            </h2>
          </div>
          
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {researchStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="font-outfit text-3xl md:text-4xl font-bold text-gradient-quantum mb-2">
                    {stat.value}
                  </div>
                  <div className="font-medium text-sm mb-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground">{stat.source}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Section */}
      <section className="relative z-10 py-20 px-6 md:px-12 lg:px-24" data-testid="countdown-section">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Calendar className="w-6 h-6 text-primary" />
                <span className="text-sm uppercase tracking-widest text-primary font-medium">
                  Conference Date
                </span>
              </div>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-2">
                April 17th, 2026
              </h2>
              <p className="text-muted-foreground">Quantum Housing Conference at Harvard University</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {countdownUnits.map((unit, index) => (
                <div 
                  key={index} 
                  className="text-center p-4 md:p-6 rounded-2xl bg-background/50"
                  data-testid={`countdown-${unit.label.toLowerCase()}`}
                >
                  <div className="font-outfit text-4xl md:text-6xl font-bold text-gradient-quantum mb-2">
                    {String(unit.value).padStart(2, '0')}
                  </div>
                  <div className="text-muted-foreground text-sm md:text-base uppercase tracking-wider">
                    {unit.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>12:00 PM EST</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>Tsai Auditorium, Harvard University</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section - Research Focused */}
      <section className="relative z-10 py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="mission-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
                Our Research
              </span>
              <h2 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                Transforming Policy Through 
                <span className="text-gradient-housing"> Research</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Our research explores how the Jock Tax—a unique tax on professional athletes' income—can 
                be redirected to fund affordable housing in the communities where athletes compete. This 
                conference brings together academics, policymakers, and athletes to discuss implementation strategies.
              </p>
              <div className="flex items-center gap-4">
                <Link
                  to="/research"
                  className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                  data-testid="explore-research-link"
                >
                  Read the Full Research
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden glass-card">
                <img
                  src="https://images.unsplash.com/photo-1755124130062-891f856c8a66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwc3VzdGFpbmFibGUlMjBhZmZvcmRhYmxlJTIwaG91c2luZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NjkzMTU0NTZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Sustainable Housing Architecture"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4 hidden md:block">
                <FileText className="w-8 h-8 text-primary mb-2" />
                <div className="font-semibold">Harvard Research</div>
                <div className="text-sm text-muted-foreground">Weatherhead Center</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Methodology Section (replacing "Powered by Quantum AI") */}
      <section className="relative z-10 py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="methodology-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Approach
            </span>
            <h2 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight">
              Research <span className="text-gradient-quantum">Methodology</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Our interdisciplinary approach combines economic modeling, policy analysis, and urban planning 
              research to understand how athlete taxation can address the global housing shortage.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {researchFeatures.map((feature, index) => (
              <div
                key={index}
                className="glass-card rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 group"
                data-testid={`methodology-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-outfit text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Partners Section - CMS Connected */}
      <PartnersSection />

      {/* CTA Section */}
      <section className="relative z-10 py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-card rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10" />
            
            <div className="relative z-10">
              <h2 className="font-outfit text-3xl md:text-5xl font-bold mb-6">
                Join the Research Community
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Be part of the conversation at the Quantum Housing Conference. Connect with researchers, 
                policymakers, and athletes working to solve the global housing crisis.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/signup"
                  data-testid="cta-register-btn"
                  className="inline-flex items-center gap-2 px-10 py-5 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(var(--primary-rgb),0.5)]"
                >
                  Register Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/sponsorship"
                  className="inline-flex items-center gap-2 px-8 py-5 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
                >
                  Become a Sponsor
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
